import { MesfModal } from "@dexteel/mesf-core";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { CommentsField } from "@/shared/fields/CommentsField";
import { modalStyles } from "@/shared/styles/modalStyles";
import { useStrandLossCodesQuery } from "../../../strand-losses-manager/hooks/queries/useStrandLossCatalogQueries";
import type { StrandLossCode } from "../../../strand-losses-manager/models/StrandAsset";

const DEFAULT_REASON_NAME = "Tundish Change";

interface JustifyStrandLossesModalProps {
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (reason: StrandLossCode, comments: string | null) => Promise<void>;
  parentAssetId: number | null;
}

interface JustifyStrandLossesFormValues {
  Comments: string;
}

const DEFAULT_VALUES: JustifyStrandLossesFormValues = {
  Comments: "",
};

export const JustifyStrandLossesModal = ({
  isSubmitting,
  onClose,
  onConfirm,
  parentAssetId,
}: JustifyStrandLossesModalProps) => {
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const { control, getValues, handleSubmit } =
    useForm<JustifyStrandLossesFormValues>({
      defaultValues: DEFAULT_VALUES,
    });
  const {
    data: strandLossCodes = [],
    error,
    isLoading,
  } = useStrandLossCodesQuery(parentAssetId, true);
  const systemReasons = strandLossCodes;
  const defaultReason = systemReasons.find(
    (reason) => reason.StrandLossCodeName === DEFAULT_REASON_NAME,
  );
  const selectedReason =
    systemReasons.find(
      (reason) => reason.StrandLossCodeId === selectedReasonId,
    ) ?? defaultReason;

  const confirmJustification: SubmitHandler<
    JustifyStrandLossesFormValues
  > = async ({ Comments }) => {
    if (!selectedReason) return;

    await onConfirm(selectedReason, Comments.trim() ? Comments : null);
  };

  return (
    <MesfModal
      title="Justify All Strand Losses"
      open
      handleClose={() => {
        if (!isSubmitting) onClose();
      }}
      id="justify-strand-losses-modal"
      maxWidth="sm"
    >
      <MesfModal.Content
        dividers
        sx={{
          ...modalStyles.modalContent,
          minWidth: { xs: "auto", sm: 520 },
          pb: 4,
        }}
      >
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Select the reason to apply to all five current strand losses.
        </Typography>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress aria-label="Loading justification reasons" />
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error">
            {error instanceof Error
              ? error.message
              : "Failed to load justification reasons"}
          </Alert>
        ) : null}

        {!isLoading && !error && systemReasons.length === 0 ? (
          <Alert severity="warning">
            No system justification reasons are available.
          </Alert>
        ) : null}

        {systemReasons.length > 0 ? (
          <Box
            role="group"
            aria-label="Strand loss justification reason"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: "center",
            }}
          >
            {systemReasons.map((reason) => {
              const isSelected =
                reason.StrandLossCodeId === selectedReason?.StrandLossCodeId;

              return (
                <Button
                  key={reason.StrandLossCodeId}
                  variant="outlined"
                  size="small"
                  aria-pressed={isSelected}
                  disabled={isSubmitting}
                  onClick={() => setSelectedReasonId(reason.StrandLossCodeId)}
                  sx={(theme) => ({
                    backgroundColor: isSelected
                      ? alpha(theme.palette.primary.main, 0.1)
                      : "transparent",
                    borderColor: isSelected ? "primary.main" : "divider",
                    color: isSelected ? "primary.main" : "text.primary",
                    fontWeight: isSelected ? 600 : 400,
                    minWidth: 140,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.06),
                      borderColor: "primary.main",
                    },
                  })}
                >
                  {reason.StrandLossCodeName}
                </Button>
              );
            })}
          </Box>
        ) : null}

        <Box sx={{ mt: 3 }}>
          <Controller
            name="Comments"
            control={control}
            render={({ field, fieldState: { error: fieldError } }) => (
              <CommentsField
                value={field.value}
                onChange={field.onChange}
                fieldError={fieldError}
                getValues={getValues}
                disabled={isSubmitting}
                rows={{ minRows: 3, maxRows: 3 }}
              />
            )}
          />
        </Box>
      </MesfModal.Content>
      <MesfModal.Actions sx={modalStyles.modalActions}>
        <Button
          variant="text"
          color="primary"
          disabled={isSubmitting}
          onClick={onClose}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedReason || isSubmitting}
          onClick={handleSubmit(confirmJustification)}
          sx={{ minWidth: 150 }}
        >
          {isSubmitting ? "Justifying..." : "Justify Losses"}
        </Button>
      </MesfModal.Actions>
    </MesfModal>
  );
};
